// Colony super-organisms. A parent anchor drifts; its member cells hold
// formation each frame. Members are ordinary consumable microbes eaten
// cell-by-cell by the normal collision path. Biting one triggers a per-layout
// reaction:
//   clump — scatter into free food (the piñata pop)
//   pair  — the survivor panics and bolts
//   ring  — the whole ring flees, shielding a central prize (the core)
//   line  — the strand whips: members flung sideways, then scatter

import type { Entity, Vec2 } from '../components'
import type { GameWorld } from '../world'
import { BIOMES, currentBiomeAt } from '../biomes'
import { morphologyFrom } from '../creature-recipe'
import { attachEyes } from './eyes'
import { spawnSporeCloud } from './hazards'
import { inWater } from '../dungeon/depth-field'
import { T } from '../tunables'

type Layout = 'clump' | 'ring' | 'pair' | 'line' | 'network'
// Random pool for the generic maybeSpawnColony. 'network' is a mushroom-floor
// set-piece spawned via maybeSpawnNetwork, so it's not in this pool.
const LAYOUTS: Layout[] = ['clump', 'ring', 'pair', 'line']

function addMember(
  world: GameWorld, parent: Entity, x: number, y: number,
  slot: number, radius: number, tint: [number, number, number],
  variant: ReturnType<typeof currentBiomeAt>, opts: { core?: boolean; bounty?: number } = {},
): Entity {
  const member: Entity = {
    position: { x, y },
    velocity: { x: 0, y: 0 },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 1.6 + Math.random(), amp: 0.05 },
    renderable: { shape: 'microbe', radius, tint, variant },
    // The core wears the gold value-tell (palette: gold = value).
    morphology: morphologyFrom({ body: 'blob', ornaments: opts.core ? ['valueGlint'] : [] }, Math.random()),
    consumable: true,
    colonyMember: { parent, slot, jitterSeed: Math.random(), ...(opts.core ? { isCore: true } : {}) },
  }
  if (opts.bounty !== undefined) member.bounty = opts.bounty
  // Little cute eyes — the "cluster of little creatures" read.
  attachEyes(member, [
    { offset: { x: -0.32, y: 0.20 }, radius: 0.40 },
    { offset: { x: 0.32, y: 0.20 }, radius: 0.40 },
  ])
  world.add(member)
  return member
}

/** Spawn a colony (parent anchor + member cells) around a point. */
export function spawnColony(world: GameWorld, x: number, y: number, forceLayout?: Layout): void {
  const c = T.colony
  const layout = forceLayout ?? LAYOUTS[Math.floor(Math.random() * LAYOUTS.length)]
  const ringN = c.ringCount
  const count =
    layout === 'pair' ? 2 :
    layout === 'ring' ? ringN + 1 :          // +1 core
    layout === 'line' ? c.lineCount : c.clumpCount

  const parent: Entity = {
    position: { x, y },
    velocity: { x: 0, y: 0 },
    wander: { impulseTimer: Math.random() * 2, noiseSeed: Math.random() * 100 },
    colony: {
      layout,
      count,
      radius: c.radius,
      spin: Math.random() * Math.PI * 2,
      spinRate: c.spinRate * (Math.random() < 0.5 ? 1 : -1),
      scattered: false,
      agitated: false,
      heading: Math.random() * Math.PI * 2,
    },
  }
  world.add(parent)

  const variant = currentBiomeAt(x, y)
  const tint = BIOMES[variant].microbeTint()
  const rMin = c.memberRadiusMin, rMax = c.memberRadiusMax
  const memberR = () => rMin + Math.random() * (rMax - rMin)

  if (layout === 'ring') {
    // The central prize, shielded by the surrounding ring.
    addMember(world, parent, x, y, -1, c.ring.coreRadius, tint, variant, { core: true, bounty: c.ring.coreBounty })
    for (let i = 0; i < ringN; i++) addMember(world, parent, x, y, i, memberR(), tint, variant)
  } else {
    const n = count
    for (let i = 0; i < n; i++) addMember(world, parent, x, y, i, memberR(), tint, variant)
  }
}

/** Maintain a small population of colonies alongside lone microbes. */
export function maybeSpawnColony(world: GameWorld, player: Entity): void {
  const c = T.colony
  const colonies = [...world.with('colony')].length
  if (colonies >= c.maxColonies) return
  if (Math.random() >= c.spawnChance) return

  const angle = Math.random() * Math.PI * 2
  const r = T.spawn.microbeSpawnRingMin + Math.random() * (T.spawn.microbeSpawnRingMax - T.spawn.microbeSpawnRingMin)
  const x = player.position.x + Math.cos(angle) * r
  const y = player.position.y + Math.sin(angle) * r
  // Whole-raft clearance: the colony drifts, so don't wedge it in a throat.
  if (!inWater(x, y, c.radius + 0.2)) return
  spawnColony(world, x, y)
}

// ── Mycelial network ─────────────────────────────────────────────────────────

/** Connected-graph edges over node offsets: an angular ring (guarantees
 *  connectivity) plus a few nearest-neighbor chords for a weby look. */
function buildNetworkEdges(offsets: Vec2[]): [number, number][] {
  const n = offsets.length
  const edges: [number, number][] = []
  const seen = new Set<string>()
  const add = (a: number, b: number) => {
    if (a === b) return
    const key = a < b ? `${a}-${b}` : `${b}-${a}`
    if (!seen.has(key)) { seen.add(key); edges.push([a, b]) }
  }
  const order = offsets
    .map((o, i) => ({ i, ang: Math.atan2(o.y, o.x) }))
    .sort((p, q) => p.ang - q.ang).map(p => p.i)
  for (let i = 0; i < n; i++) add(order[i], order[(i + 1) % n])
  for (let i = 0; i < n; i++) {
    let best = -1, bd = Infinity
    for (let j = 0; j < n; j++) {
      if (j === i) continue
      const dx = offsets[i].x - offsets[j].x, dy = offsets[i].y - offsets[j].y
      const d = dx * dx + dy * dy
      if (d < bd) { bd = d; best = j }
    }
    if (best >= 0 && Math.random() < 0.5) add(i, best)
  }
  return edges
}

/** Graph distance (hops) from `start` to every node; Infinity if unreachable. */
function bfsDistances(count: number, edges: [number, number][], start: number): number[] {
  const adj: number[][] = Array.from({ length: count }, () => [])
  for (const [a, b] of edges) { adj[a].push(b); adj[b].push(a) }
  const dist = new Array<number>(count).fill(Infinity)
  dist[start] = 0
  const q = [start]
  while (q.length) {
    const u = q.shift()!
    for (const v of adj[u]) if (dist[v] === Infinity) { dist[v] = dist[u] + 1; q.push(v) }
  }
  return dist
}

/** Spawn a mycelial network: a drifting raft of mushroom nodes joined by threads. */
export function spawnNetwork(world: GameWorld, x: number, y: number): void {
  const c = T.colony, cfg = c.network
  const n = cfg.nodeCount
  const offsets: Vec2[] = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.7
    const rr = c.radius * (0.5 + Math.random() * 0.8)
    offsets.push({ x: Math.cos(a) * rr, y: Math.sin(a) * rr })
  }
  const parent: Entity = {
    position: { x, y },
    velocity: { x: 0, y: 0 },
    wander: { impulseTimer: Math.random() * 2, noiseSeed: Math.random() * 100 },
    colony: {
      layout: 'network', count: n, radius: c.radius, spin: 0, spinRate: 0,
      scattered: false, agitated: false, heading: 0,
      nodeOffsets: offsets, edges: buildNetworkEdges(offsets),
      rippleFrom: -1, rippleTime: 0, nodeDist: [], nodePuffed: [],
    },
  }
  world.add(parent)

  const variant = currentBiomeAt(x, y)
  const tint = BIOMES[variant].microbeTint()
  for (let i = 0; i < n; i++) {
    const node: Entity = {
      position: { x: x + offsets[i].x, y: y + offsets[i].y },
      velocity: { x: 0, y: 0 },
      wobble: { phase: Math.random() * Math.PI * 2, freq: 1.2, amp: 0.05 },
      renderable: { shape: 'microbe', radius: cfg.nodeRadius, tint, variant },
      // Clean cap+gills (no per-node hyphae ornament) so the drawn connector
      // threads are the ONLY strands — the web reads as nodes joined, not a
      // clump of fuzzy mushrooms.
      morphology: morphologyFrom({ body: 'mushroom', ornaments: ['gills'], count2: 6 }, Math.random()),
      consumable: true,
      colonyMember: { parent, slot: i, jitterSeed: Math.random() },
    }
    attachEyes(node, [
      { offset: { x: -0.24, y: 0.28 }, radius: 0.26 },
      { offset: { x: 0.24, y: 0.28 }, radius: 0.26 },
    ])
    world.add(node)
  }
}

/** Maintain a lone mycelial network in the mushroom floor around the player. */
export function maybeSpawnNetwork(world: GameWorld, player: Entity): void {
  if (currentBiomeAt(player.position.x, player.position.y) !== 'mushroom') return
  const cfg = T.colony.network
  let existing = 0
  for (const p of world.with('colony')) if (p.colony.layout === 'network') existing++
  if (existing >= cfg.maxNetworks) return
  if (Math.random() >= cfg.spawnChance) return
  const a = Math.random() * Math.PI * 2
  const r = T.spawn.microbeSpawnRingMin + Math.random() * (T.spawn.microbeSpawnRingMax - T.spawn.microbeSpawnRingMin)
  const x = player.position.x + Math.cos(a) * r
  const y = player.position.y + Math.sin(a) * r
  if (!inWater(x, y, T.colony.radius + 0.2)) return
  spawnNetwork(world, x, y)
}

/** Ripple reaction: biting a node snaps its threads and sends a spore-puff
 *  wavefront out along the graph, each node puffing as the front reaches it. */
function updateNetwork(
  world: GameWorld, parent: Entity, members: Entity[], dt: number, elapsed: number,
): void {
  const col = parent.colony!
  const cfg = T.colony.network

  // A node being eaten (or a freshly lost node) starts a ripple from its slot.
  const bitten = members.find(m => m.absorbing)
  if (bitten) {
    const slot = bitten.colonyMember!.slot
    if (col.rippleFrom !== slot) {
      col.rippleFrom = slot
      col.rippleTime = 0
      col.nodeDist = bfsDistances(col.count, col.edges ?? [], slot)
      col.nodePuffed = new Array<boolean>(col.count).fill(false)
      col.nodePuffed[slot] = true // the bitten node is being eaten, not puffing
      col.edges = (col.edges ?? []).filter(([a, b]) => a !== slot && b !== slot)
      col.agitated = true
    }
  }

  // Advance the wavefront and puff each node as its graph-distance is reached.
  if (col.rippleFrom !== undefined && col.rippleFrom >= 0) {
    col.rippleTime = (col.rippleTime ?? 0) + dt
    const dist = col.nodeDist ?? []
    const puffed = col.nodePuffed ?? []
    for (const m of members) {
      const s = m.colonyMember!.slot
      if (puffed[s]) continue
      const d = dist[s]
      if (d !== undefined && d !== Infinity && d * cfg.hopDelay <= col.rippleTime) {
        puffed[s] = true
        spawnSporeCloud(world, m.position.x, m.position.y)
      }
    }
  }

  // Formation: nodes hold their fixed offset relative to the drifting parent,
  // with a gentle bob (a touch livelier once the web has been disturbed).
  const k = Math.min(1, T.colony.followRate * dt)
  const offs = col.nodeOffsets ?? []
  const bob = cfg.bobAmp * (col.agitated ? 1.8 : 1)
  for (const m of members) {
    const s = m.colonyMember!.slot
    const o = offs[s] ?? { x: 0, y: 0 }
    const tx = parent.position.x + o.x + Math.sin(elapsed * 1.5 + s) * bob
    const ty = parent.position.y + o.y + Math.cos(elapsed * 1.3 + s) * bob
    m.position.x += (tx - m.position.x) * k
    m.position.y += (ty - m.position.y) * k
  }
}

export function colonySystem(world: GameWorld, player: Entity, dt: number, elapsed: number): void {
  const cfg = T.colony
  const despawn2 = T.spawn.microbeDespawnDistance * T.spawn.microbeDespawnDistance
  for (const parent of world.with('colony', 'position')) {
    const col = parent.colony
    const members = [...world.with('colonyMember', 'position')].filter(
      m => m.colonyMember.parent === parent,
    )

    if (members.length === 0) {
      if (world.id(parent) !== undefined) world.remove(parent)
      continue
    }
    if (col.scattered) continue

    // Drifted far behind the player → retire the whole colony.
    const ddx = parent.position.x - player.position.x
    const ddy = parent.position.y - player.position.y
    if (ddx * ddx + ddy * ddy > despawn2) {
      for (const m of members) if (world.id(m) !== undefined) world.remove(m)
      if (world.id(parent) !== undefined) world.remove(parent)
      continue
    }

    // Synchronized breathing: members share a pulse phase (+ a small per-member
    // offset) so the cluster reads as one organism, not loose blobs.
    const colPhase = elapsed * 1.6
    for (const m of members) {
      if (m.wobble) m.wobble.phase = colPhase + m.colonyMember!.jitterSeed * 0.6
    }

    // A mycelial network never scatters — it ripples spores instead.
    if (col.layout === 'network') {
      updateNetwork(world, parent, members, dt, elapsed)
      continue
    }

    const disturbed = members.some(m => m.absorbing) || members.length < col.count

    if (col.layout === 'ring') {
      updateRing(world, parent, members, player, disturbed, dt)
      continue
    }
    if (disturbed) {
      scatter(world, parent, members, col.layout)
      continue
    }

    // Cohesive formation (clump / pair / line).
    col.spin += col.spinRate * dt
    const k = Math.min(1, cfg.followRate * dt)
    for (const m of members) {
      const t = formationTarget(parent, m, elapsed)
      m.position.x += (t.x - m.position.x) * k
      m.position.y += (t.y - m.position.y) * k
    }
  }
}

// ── Ring: the disturbed ring flees while shielding the core ───────────────────
function updateRing(
  world: GameWorld, parent: Entity, members: Entity[], player: Entity,
  disturbed: boolean, dt: number,
): void {
  const col = parent.colony!
  const cfg = T.colony
  if (disturbed) col.agitated = true

  if (col.agitated) {
    // The whole colony bolts away from the player, carrying the core.
    const dx = parent.position.x - player.position.x
    const dy = parent.position.y - player.position.y
    const d = Math.hypot(dx, dy) || 1
    const flee = cfg.ring.fleeSpeed
    if (parent.velocity) {
      parent.velocity.x += ((dx / d) * flee - parent.velocity.x) * Math.min(1, 3 * dt)
      parent.velocity.y += ((dy / d) * flee - parent.velocity.y) * Math.min(1, 3 * dt)
    }
    col.spin += (col.spinRate > 0 ? 1 : -1) * cfg.ring.agitatedSpin * dt
  } else {
    col.spin += col.spinRate * dt
  }

  const ring = members.filter(m => !m.colonyMember!.isCore)
  const core = members.find(m => m.colonyMember!.isCore)

  // Core exposed (ring eaten through) → it's just a lone high-value cell now:
  // it breaks away and flees on its own.
  if (core && ring.length === 0) {
    const dx = core.position.x - player.position.x
    const dy = core.position.y - player.position.y
    const d = Math.hypot(dx, dy) || 1
    if (core.velocity) { core.velocity.x = (dx / d) * cfg.ring.fleeSpeed; core.velocity.y = (dy / d) * cfg.ring.fleeSpeed }
    world.removeComponent(core, 'colonyMember')
    if (!core.wander) world.addComponent(core, 'wander', { impulseTimer: 0.5, noiseSeed: Math.random() * 100 })
    if (world.id(parent) !== undefined) world.remove(parent)
    return
  }

  const k = Math.min(1, cfg.followRate * dt)
  // Space by ring-member count (count − 1, excluding the central core) so the
  // ring closes fully around the core instead of leaving a wedge open.
  const step = (Math.PI * 2) / Math.max(1, col.count - 1)
  for (const m of ring) {
    const a = col.spin + m.colonyMember!.slot * step
    const tx = parent.position.x + Math.cos(a) * col.radius
    const ty = parent.position.y + Math.sin(a) * col.radius
    m.position.x += (tx - m.position.x) * k
    m.position.y += (ty - m.position.y) * k
  }
  if (core) {
    core.position.x += (parent.position.x - core.position.x) * k
    core.position.y += (parent.position.y - core.position.y) * k
  }
}

function formationTarget(parent: Entity, m: Entity, elapsed: number): { x: number; y: number } {
  const col = parent.colony!
  const cm = m.colonyMember!
  const px = parent.position.x
  const py = parent.position.y
  if (col.layout === 'pair') {
    const step = (Math.PI * 2) / col.count
    const a = col.spin + cm.slot * step
    return { x: px + Math.cos(a) * col.radius, y: py + Math.sin(a) * col.radius }
  }
  if (col.layout === 'line') {
    // Strung along the heading, centered, with a gentle traveling undulation.
    const t = (cm.slot / Math.max(1, col.count - 1) - 0.5) * 2 // −1..1
    const along = t * col.radius * 1.8
    const dir = { x: Math.cos(col.heading), y: Math.sin(col.heading) }
    const perp = { x: -dir.y, y: dir.x }
    const wave = Math.sin(elapsed * 1.4 + cm.slot * 0.9) * 0.06
    return {
      x: px + dir.x * along + perp.x * wave,
      y: py + dir.y * along + perp.y * wave,
    }
  }
  // clump — a soft rosette: cells spaced evenly by slot (radial Haeckel read)
  // at slightly varied radii, with a gentle shared sway.
  const step = (Math.PI * 2) / col.count
  const a = col.spin * 0.4 + cm.slot * step + Math.sin(elapsed * 0.6 + cm.slot) * 0.12
  const rr = col.radius * (0.55 + 0.35 * ((cm.jitterSeed * 3.7) % 1))
  return { x: px + Math.cos(a) * rr, y: py + Math.sin(a) * rr }
}

function scatter(world: GameWorld, parent: Entity, members: Entity[], layout: Layout): void {
  parent.colony!.scattered = true
  const cfg = T.colony
  const speed = layout === 'pair' ? cfg.pairPanicSpeed : cfg.scatterSpeed
  const heading = parent.colony!.heading
  for (const m of members) {
    if (m.absorbing) continue
    let dx = m.position.x - parent.position.x
    let dy = m.position.y - parent.position.y
    const d = Math.hypot(dx, dy)
    if (d < 0.0001) {
      const a = Math.random() * Math.PI * 2
      dx = Math.cos(a); dy = Math.sin(a)
    } else {
      dx /= d; dy /= d
    }
    if (m.velocity) {
      if (layout === 'line') {
        // Whip: flung perpendicular to the strand, alternating sides.
        const side = (m.colonyMember!.slot % 2 === 0) ? 1 : -1
        m.velocity.x = -Math.sin(heading) * side * cfg.line.whipSpeed + dx * 0.3
        m.velocity.y = Math.cos(heading) * side * cfg.line.whipSpeed + dy * 0.3
      } else {
        m.velocity.x = dx * speed
        m.velocity.y = dy * speed
      }
    }
    world.removeComponent(m, 'colonyMember')
    if (!m.wander) {
      world.addComponent(m, 'wander', { impulseTimer: 0.6 + Math.random(), noiseSeed: Math.random() * 100 })
    }
  }
  if (world.id(parent) !== undefined) world.remove(parent)
}
